<template>
    <BaseWidget
        :id="id"
        :widget-name="widgetName"
        :has-content="!!content"
        :is-loading="isLoading"
        :is-expanded="isExpanded"
        :configuration-data="configurationData"
        :widget-type="widgetType"
        :widget-last-updated="widgetLastUpdated"
        :needs-attention="needsAttention"
        v-on="$listeners"
    >
        <div v-show="!isLoading" class="widget-content">
            <div class="widget-inner">
                <div class="average-time-widget">
                    <div class="widget-chart-label">
                        <span>{{ $t('dashboard.widgetType7.time') }}</span>
                    </div>
                    <div
                        ref="widgetChartRef"
                        :class="[
                            'average-time-widget-chart',
                            configuration.processing_time === 1 ? 'response' : 'duration',
                        ]"
                    ></div>
                </div>
            </div>
        </div>
    </BaseWidget>
</template>

<script type="text/babel">
import { mapState, mapGetters } from 'vuex';
import Chartist from 'ascom-chartist-logscale';
import BaseWidget from '../BaseWidget';
import debounce from 'lodash-es/debounce';
import {
    processing,
    getTranslationKeyByOption,
    getCallTypeNameById,
    getPreviousWeekArray,
} from '../widgetHelpers';
import WidgetConfigurationTable from './common/WidgetConfigurationTable.vue';
import widgetInterface from './common/widgetInterface';
import { mounted, beforeDestroy } from './common/chartsLifecycleHooks';
import { configurationData } from './common/widgetsComputedProps';
import { makeChart } from './common/chartingHelpers';
import { secsToReadableTime } from '../../common/helpers';
import { DEBOUNCE_CHARTS_REFRESH } from '../../../constants';

export default {
    components: {
        BaseWidget,
        WidgetConfigurationTable,
    },

    props: widgetInterface,

    data() {
        return {
            instanceChart: null,
            debouncedMakeChart: debounce(() => this.makeChart(), DEBOUNCE_CHARTS_REFRESH),
            needsAttention: false,
        };
    },

    computed: {
        ...mapGetters('user', ['userType']),
        ...mapState({
            navCollapsed: 'navCollapsed',
            callTypes: 'callTypes',
            allAssignmentAreas: 'assignmentArea',
        }),

        configurationData,

        /**
         * Returns widget name
         * @returns {string}
         */
        widgetName() {
            return this.configuration.name || this.$t('dashboard.widgetType7.name');
        },

        /**
         * Returns name of the configured call type
         * @returns {string}
         */
        callType() {
            return getCallTypeNameById(this.callTypes, this.configuration.call_type);
        },

        /**
         * Returns configured processing time parameter
         * @returns {string}
         */
        processingTime() {
            return this.$t(getTranslationKeyByOption(processing, this.configuration.processing_time));
        },

        /**
         * Generates object for line chart data with labels for tooltips
         * @returns {Object} data for line chart
         */
        chartData() {
            if (!this.content) {
                return [];
            }

            let labels = getPreviousWeekArray(new Date());

            return {
                labels,
                series: [
                    this.content.map((value, i) => ({
                        meta: labels[i],
                        value,
                    })),
                ],
            };
        },
    },

    watch: {
        navCollapsed() {
            this.debouncedMakeChart();
        },
    },

    mounted,

    beforeDestroy,

    methods: {
        makeChart,

        /**
         * Draws line chart
         * @param ref
         * @param data
         */
        drawChart(ref, data) {
            let options = {
                lineSmooth: false,
                height: this.isPrintver ? 300 : undefined,
                width: this.isPrintver ? 600 : undefined,
                axisX: {
                    showGrid: false,
                    offset: 20,
                },
                axisY: {
                    low: 0,
                    offset: 70,
                },
                plugins: [
                    Chartist.plugins.tooltip({
                        transformTooltipTextFnc: secsToReadableTime,
                    }),
                ],
                chartPadding: {
                    top: 15,
                    right: 0,
                    bottom: 0,
                    left: 0,
                },
            };
            if (this.instanceChart) {
                this.instanceChart.update(data, options);
            } else {
                this.instanceChart = new Chartist.Line(ref, data, options);
            }
            window.addEventListener('resize', this.debouncedMakeChart);
        },

        setNeedsAttention(value) {
            this.needsAttention = value;
        },
    },
};
</script>
